import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Package, 
  Users, 
  Truck, 
  Tag, 
  ShoppingCart,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Calendar,
  Printer,
  Ticket,
  Shield,
  UserPlus,
  ClipboardCheck,
  Ruler,
  Calculator,
  FolderOpen,
  FileSearch,
  Wallet,
  CreditCard
} from 'lucide-react';
import { Button } from './ui/button';

const navItems = [
  { path: '/dashboard', label: 'ダッシュボード', icon: LayoutDashboard },
  { divider: true },
  { path: '/orders', label: '注文一覧', icon: ShoppingCart },
  { path: '/proxy-order', label: '代理発注', icon: UserPlus },
  { path: '/tewari-box', label: '手割りボックス', icon: Package },
  { path: '/print', label: '帳票印刷', icon: Printer },
  { divider: true },
  { path: '/products', label: '商品マスタ', icon: Package },
  { path: '/master', label: 'マスタ管理', icon: FolderOpen },
  { path: '/customers', label: '取引先', icon: Users },
  { path: '/suppliers', label: '仕入れ先', icon: Truck },
  { path: '/order-dates', label: '配送可能日', icon: Calendar },
  { divider: true },
  { path: '/order-confirm', label: '伝票作成', icon: ClipboardCheck },
  { path: '/profit', label: '仕入れ登録', icon: Calculator },
  { path: '/payment-prep', label: '支払い準備', icon: Wallet },
  { path: '/credit-payments', label: '掛け金管理', icon: CreditCard },
  { divider: true },
  { path: '/admin/users', label: '管理者ユーザー', icon: Shield },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // 管理画面のタイトルを設定
  useEffect(() => {
    document.title = 'やさまるFish管理画面';
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentPage = navItems.find(item => location.pathname.startsWith(item.path));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-layout">
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 h-16 flex items-center px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
          <Menu className="h-5 w-5" />
        </Button>
        <span className="ml-3 font-semibold text-slate-900">やさまるFish管理画面</span>
      </header>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed top-0 left-0 z-50 h-screen w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} flex flex-col`}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <h1 className="font-bold text-lg text-slate-900">やさまるFish</h1>
            <p className="text-xs text-slate-500">今日も鮮度最強でいこう</p>
          </div>
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="p-4 space-y-1 flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {navItems.map((item, idx) => {
            if (item.divider) {
              return <div key={`divider-${idx}`} className="border-t border-slate-200 my-2" />;
            }
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium flex-1">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
              <span className="text-sm font-medium text-slate-600">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            ログアウト
          </Button>
        </div>
      </aside>

      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen flex flex-col">
        <div className="bg-white border-b border-slate-200 px-6 py-3 flex-shrink-0">
          <div className="flex items-center text-sm text-slate-500">
            <Link to="/dashboard" className="hover:text-slate-900">ホーム</Link>
            {currentPage && currentPage.path !== '/dashboard' && (
              <>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-slate-900">{currentPage.label}</span>
              </>
            )}
          </div>
        </div>
        <div className="p-6 lg:p-8 flex-1 overflow-y-auto">{children}</div>
      </main>
    </div>
  );
};
